import { useFormik } from "formik";
import { observer } from "mobx-react";
import type React from "react";
import { useTranslation } from "react-i18next";

import { TeacherModals } from "constants/modals-constants";

import { useGroups } from "hooks/useGroups";
import { useModal } from "hooks/useModal";

import { InputWithErrorMessage } from "components/InputWithErrorMessage";
import { ModalRenderType } from "components/Modal/Modal.model";

import { Heading2 } from "styles/elements/Headings";
import { ParagraphLarge } from "styles/elements/Texts";
import { CssButton, CssFlex } from "styles/helpers/layout";

import { type RenameGroupFormFields, buildRenameGroupFormSchema } from "./RenameGroupModal.schema";
import { StyledModal } from "./RenameGroupModal.styled";

interface RenameGroupModalProps {
  groupId: number;
  groupName: string;
}

export const RenameGroupModal: React.FC<RenameGroupModalProps> = observer(
  ({ groupId, groupName }) => {
    const modalId = TeacherModals.RenameGroup;

    const { t } = useTranslation("groups");
    const { closeModal, isModalOpen } = useModal();
    const { editGroupName } = useGroups();

    const isModalCurrentlyOpen = isModalOpen(modalId);

    const formik = useFormik<RenameGroupFormFields>({
      enableReinitialize: true,
      initialValues: {
        name: groupName,
      },
      validationSchema: buildRenameGroupFormSchema(),
      validateOnChange: true,
      onSubmit: async (values) => {
        await editGroupName(groupId, values.name);

        // Reset form fields after submit is complete
        formik.resetForm();
        closeModal();
      },
    });

    return (
      <StyledModal
        dataCy={modalId}
        isOpen={isModalCurrentlyOpen}
        renderToElementType={ModalRenderType.REACT_PORTAL}
        onClose={closeModal}
      >
        <form noValidate onSubmit={formik.handleSubmit}>
          <CssFlex flexDirection="column" gap={1} padding="2rem">
            <Heading2>{t("groups:groupDetails.renameGroupModal.title")}</Heading2>
            <ParagraphLarge>{t("groups:groupDetails.renameGroupModal.description")}</ParagraphLarge>
            <InputWithErrorMessage
              aria-label={t("groups:groupDetails.renameGroupModal.fields.name.label")}
              dataCy="group-name-input"
              errorMessage={formik.errors.name}
              hasError={formik.dirty && !!formik.errors.name}
              id="rename-group-modal-name-input"
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
            />
            <CssFlex justifyContent="space-between" marginTop={2} gap={1}>
              <CssButton disabled={formik.isSubmitting} onClick={closeModal}>
                {t("groups:groupDetails.renameGroupModal.cancelButton")}
              </CssButton>
              <CssButton
                data-cy="apply-changes-button"
                variant="primary"
                disabled={!formik.dirty || !formik.isValid}
                isLoading={formik.isSubmitting}
                onClick={formik.submitForm}
              >
                {t("groups:groupDetails.renameGroupModal.confirmButton")}
              </CssButton>
            </CssFlex>
          </CssFlex>
        </form>
      </StyledModal>
    );
  },
);
